<template>
  <div v-if="isOpen">
    <Modal :is-open="isOpen">
      <div class="flex items-center gap-3">
        <div class="flex items-center justify-center p-2 bg-blue-100 rounded-full">
          <CloudDownloadIcon class="w-6 h-6 text-blue-600" aria-hidden="true" />
        </div>
        <div>
          <h3 id="dataSyncJobsTitle" class="text-lg font-bold leading-6 text-gray-900">
            {{ $t("Latest Background Data Sync Jobs for") }} {{ service.title }}
          </h3>
          <p class="text-sm text-gray-600">
            {{ $t("Here are the latest background data sync jobs for") }} {{ service.title }}.
          </p>
        </div>
      </div>

      <div class="w-full">
        <div v-if="service.data_sync_jobs.length > 0" class="mt-4">
          <div class="max-h-96 overflow-auto pr-1 custom-scrollbar">
            <TransitionGroup name="job-list" tag="div" class="space-y-3">
              <div
                v-for="job in service.data_sync_jobs"
                :key="job.id"
                class="p-4 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-200">
                <div class="flex flex-col md:flex-row md:items-center gap-3">
                  <!-- Status indicator and progress bar -->
                  <div class="w-full md:w-5/12">
                    <div class="flex items-center gap-3">
                      <div class="relative">
                        <div class="h-10 w-10 rounded-full flex items-center justify-center" :class="jobStatusBgClass(job.status)">
                          <component :is="jobStatusIcon(job.status)" class="h-5 w-5" :class="jobStatusIconClass(job.status)" />
                        </div>
                      </div>
                      <div class="flex-1">
                        <h4 class="font-medium text-gray-900 text-sm">
                          {{ job.stats.objects.map((o) => o.title()).join(", ") }}
                        </h4>
                        <div class="flex items-center mt-1">
                          <span class="text-xs font-medium" :class="jobStatusClass(job.status)">
                            {{ job.status.title() }}
                          </span>
                          <span v-if="job.status === 'running' || job.status === 'pending'" class="text-xs text-gray-500 ml-2">
                            ({{ job.progress }}%)
                          </span>
                        </div>
                        <!-- Progress bar -->
                        <div v-if="job.status === 'running' || job.status === 'pending'" class="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                          <div
                            class="h-1.5 rounded-full transition-all duration-300 ease-in-out"
                            :class="job.status === 'running' ? 'bg-blue-500' : 'bg-yellow-400'"
                            :style="{ width: `${job.progress}%` }" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Timing information -->
                  <div class="md:w-4/12 space-y-1 py-2 border-t md:border-t-0 md:border-l md:pl-4 md:py-0 border-gray-100">
                    <div class="flex items-center text-xs text-gray-600">
                      <ClockIcon class="w-3.5 h-3.5 mr-1.5" />
                      <span class="font-medium mr-1">{{ $t("Started") }}:</span> {{ formatDate(job.started_at) }}
                    </div>
                    <div class="flex items-center text-xs text-gray-600">
                      <CheckCircleIcon class="w-3.5 h-3.5 mr-1.5" />
                      <span class="font-medium mr-1">{{ $t("Ended") }}:</span> {{ formatDate(job.ended_at) }}
                    </div>
                    <div class="flex items-center text-xs text-gray-600">
                      <TimerIcon class="w-3.5 h-3.5 mr-1.5" />
                      <span class="font-medium mr-1">{{ $t("Duration") }}:</span> {{ calculateDuration(job.started_at, job.ended_at) }}
                    </div>
                  </div>

                  <!-- Action buttons -->
                  <div class="md:w-3/12 flex justify-end">
                    <button
                      v-if="job.status === 'running' || job.status === 'pending'"
                      type="button"
                      class="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                      aria-label="Abort job"
                      @click="abortJob(job)">
                      <XCircleIcon class="w-4 h-4 mr-1.5" />
                      {{ $t("Abort") }}
                    </button>
                    <button
                      v-else-if="job.status !== 'running'"
                      type="button"
                      class="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      aria-label="Re-run job"
                      @click="reRunJob(job)">
                      <RefreshCwIcon class="w-4 h-4 mr-1.5" />
                      {{ $t("Re Run") }}
                    </button>
                  </div>
                </div>
              </div>
            </TransitionGroup>
          </div>
        </div>
        <div v-else class="flex flex-col items-center justify-center py-10 text-center">
          <InboxIcon class="w-12 h-12 text-gray-300 mb-3" />
          <p class="text-md text-gray-500 font-medium">
            {{ $t("No sync jobs found for this integration.") }}
          </p>
          <p class="text-sm text-gray-400 mt-1">
            {{ $t("Jobs will appear here once they are created.") }}
          </p>
        </div>
      </div>
      <div class="flex justify-end mt-2 mr-1">
        <button
          type="button"
          class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          aria-label="Close dialog"
          @click.prevent="$emit('update:isOpen', false)">
          <X class="w-4 h-4 mr-2" />
          {{ $t("Close") }}
        </button>
      </div>
    </Modal>
  </div>
</template>

<script>
import IntegrationService from "@/services/integration.service"
import Modal from "@/components/shared/Modal.vue"
import {
  CloudDownloadIcon,
  RefreshCwIcon,
  XCircleIcon,
  ClockIcon,
  CheckCircleIcon,
  TimerIcon,
  X,
  Loader2Icon,
  CheckCircle2Icon,
  AlertTriangleIcon,
  InboxIcon
} from "lucide-vue-next"

export default {
  name: "DataSyncJobs",
  components: {
    Modal,
    Loader2Icon,
    CheckCircle2Icon,
    AlertTriangleIcon,
    CloudDownloadIcon,
    RefreshCwIcon,
    XCircleIcon,
    ClockIcon,
    CheckCircleIcon,
    TimerIcon,
    X,
    InboxIcon
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    service: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ["update:isOpen"],
  data() {
    return {
      selected_service: null,
      refreshInterval: null
    }
  },
  watch: {
    service: {
      handler(newVal) {
        this.selected_service = newVal
      },
      immediate: true
    },
    isOpen(newVal) {
      if (newVal) {
        this.startRefreshInterval()
      } else {
        this.clearRefreshInterval()
      }
    }
  },

  async created() {
    this.selected_service = this.service
    this.startRefreshInterval()
  },
  beforeUnmount() {
    this.clearRefreshInterval()
  },
  methods: {
    jobStatusClass(status) {
      switch (status) {
        case "pending":
          return "text-yellow-500"
        case "running":
          return "text-blue-500"
        case "completed":
          return "text-green-500"
        case "aborted":
          return "text-gray-500"
        case "failed":
          return "text-red-500"
        default:
          return "text-gray-500"
      }
    },
    jobStatusBgClass(status) {
      switch (status) {
        case "pending":
          return "bg-yellow-100"
        case "running":
          return "bg-blue-100"
        case "completed":
          return "bg-green-100"
        case "aborted":
          return "bg-gray-100"
        case "failed":
          return "bg-red-100"
        default:
          return "bg-gray-100"
      }
    },
    jobStatusIconClass(status) {
      switch (status) {
        case "pending":
          return "text-yellow-500"
        case "running":
          return "text-blue-500"
        case "completed":
          return "text-green-500"
        case "aborted":
          return "text-gray-500"
        case "failed":
          return "text-red-500"
        default:
          return "text-gray-500"
      }
    },
    jobStatusIcon(status) {
      switch (status) {
        case "pending":
          return AlertTriangleIcon
        case "running":
          return Loader2Icon
        case "completed":
          return CheckCircle2Icon
        case "aborted":
          return XCircleIcon
        case "failed":
          return AlertTriangleIcon
        default:
          return AlertTriangleIcon
      }
    },
    formatDate(date) {
      if (date) {
        return this.$moment(date).format("YYYY-MM-DD HH:mm:ss")
      }
      return "N/A"
    },
    calculateDuration(started_at, ended_at) {
      if (started_at) {
        if(!ended_at){
          ended_at = new Date().toISOString()
        }
        const start = this.$moment(started_at)
        const end = this.$moment(ended_at)
        const duration = this.$moment.duration(end.diff(start))

        return `${duration.hours()}h ${duration.minutes()}m ${duration.seconds()}s`
      }
      return "N/A"
    },
    showToast(message, type, timeout = 3000) {
      this.$toast.open({
        message: message,
        position: "top",
        type: type,
        duration: timeout
      })
    },
    startRefreshInterval() {
      this.clearRefreshInterval()
      this.refreshInterval = setInterval(() => {
        if (this.selected_service && this.selected_service.id) {
          this.refetchService()
        }
      }, 5000) // Fetch every 5 seconds
    },
    clearRefreshInterval() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval)
        this.refreshInterval = null
      }
    },
    refetchService() {
      IntegrationService.getService(this.selected_service.id).then((res) => {
        this.selected_service = res.data
      })
    },
    abortJob(job) {
      IntegrationService.abortSyncJob(this.selected_service.id, job.id)
        .then(() => {
          this.showToast(this.$t("Job has been aborted"), "success")
          // update the status of the job 
          job.status = "aborted"
          job.progress = 0
          job.ended_at = new Date().toISOString()
        })
        .catch((error) => {
          this.showToast(this.$t("Failed to abort job") + ": " + error.message, "error")
        })
    },
    reRunJob(job) {
      IntegrationService.reRunSyncJob(this.selected_service.id, job.id)
        .then(() => {
          this.showToast(this.$t("Job has been re-run"), "success")
          // update the status of the job
          job.status = "running"
          job.progress = 0
          job.started_at = new Date().toISOString()
          job.ended_at = null
        })
        .catch((error) => {
          this.showToast(this.$t("Failed to re-run job") + ": " + error.message, "error")
        })
    }
  }
}
</script>

<style scoped>
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 20px;
}

/* Transition animations for job list */
.job-list-enter-active,
.job-list-leave-active {
  transition: all 0.3s ease;
}

.job-list-enter-from,
.job-list-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
